<template>
  <div class="wrapper">
    <div v-if="!disabled">
      <div class="addAttachment">
        <input
          :id="`fileInputOrder${order.id}`"
          type="file"
          multiple
          @change="event => fileBrowseHandler(event)"
        />
        <label
          :for="`fileInputOrder${order.id}`"
          v-on:drop="event => fileDropHandler(event)"
          v-on:dragover="event => fileDragHandler(event)"
          v-on:dragleave="event => fileDragLeaveHandler(event)"
        >
          {{ $t('orderList.attachmentsDropArea') }}
        </label>
      </div>
      <p class="addAttachmentError">
        {{ order._attachmentError }}
      </p>
    </div>
    <div class="attachmentList">
      <ul>
        <li
          v-for="attachment in order.attachments"
          :key="attachment.id"
        >
          <a
            class="attachmentLink"
            :href="`/api/order/${this.order.id}/attachment/${attachment.id}`"
            target="_blank"
          >
            <font-awesome-icon :icon="getFileIcon(attachment.name)" />
            {{ attachment.name }}
          </a>
          <button
            type="button"
            class="attachmentDelete"
            @click="fileDelete(attachment.id)"
          >
            <font-awesome-icon icon="xmark"/>
          </button>
        </li>
      </ul>
    </div>
  </div>
</template>

<style scoped>
  .wrapper {
    display: flex;
    flex-wrap: wrap;
  }

  .wrapper > div {
    flex: 1 0 350px;
    display: flex;
    flex-direction: column;
    box-sizing: border-box;
    padding: 20px;
  }

  .addAttachment > input {
    display: none;
  }

  .addAttachment > label {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 100%;
    height: 150px;
    font-weight: 600;
    color: var(--secondary-text-color);
    border: 1px solid var(--primary-color);
    border-radius: 5px;
    cursor: pointer;
    transition-property: border-color;
    transition-duration: 0.2s;
  }

  .addAttachment > label:hover,
  .addAttachment > label.dragActive {
    border-color: var(--secondary-color);
  }

  .addAttachmentError {
    margin: 10px 0;
    padding: 0;
    color: var(--error-color);
  }

  .attachmentList > ul {
    margin: 0;
    padding: 0;
    list-style-type: none;
  }

  .attachmentList > ul > li {
    margin-bottom: 10px;
    word-break: break-all;
  }

  .attachmentLink {
    padding-bottom: 1px;
    color: var(--primary-color);
    text-decoration: none;
    background-color: transparent;
    border-bottom: 1px solid transparent;
    cursor: pointer;
    transition-property: color, border-bottom-color;
    transition-duration: 0.2s;
  }
  
  .attachmentLink:hover {
    color: var(--secondary-color);
    border-bottom-color: var(--secondary-color);
  }

  .attachmentDelete {
    margin-left: 10px;
    padding: 0;
    vertical-align: middle;
    color: var(--secondary-text-color);
    background-color: transparent;
    border: none;
    cursor: pointer;
    transition-property: color;
    transition-duration: 0.2s;
  }
  
  .attachmentDelete:hover {
    color: var(--secondary-color);
  }
</style>

<script>
  import mime from 'mime';

  export default {
    props: [ 'order', 'disabled' ],
    methods: {
      fileDropHandler(event) {
        event.preventDefault();

        if (event.dataTransfer.items) {
          for (let i = 0; i < event.dataTransfer.items.length; i++) {
            if (event.dataTransfer.items[i].kind === 'file') {
              let file = event.dataTransfer.items[i].getAsFile();
              this.fileUpload(file);
            }
          }
        } else {
          for (let i = 0; i < event.dataTransfer.files.length; i++) {
            this.fileUpload(file);
          }
        }

        event.target.classList.remove('dragActive');
      },
      fileDragLeaveHandler(event) {
        event.preventDefault();
        event.target.classList.remove('dragActive');
      },
      fileDragHandler(event) {
        event.preventDefault();
        event.target.classList.add('dragActive');
      },
      fileDelete: async function (attachmentId) {
        const index = this.order.attachments.findIndex(attachment => attachment.id === attachmentId);
        await fetch(`/api/order/${this.order.id}/attachment/${attachmentId}`, {
          method: 'DELETE'
        });
        this.order.attachments.splice(index, 1);
      },
      fileBrowseHandler(event) {
        const files = Array.from(event.target.files);
        files.forEach(function (file) {
          this.fileUpload(file)
        }.bind(this));
        event.target.value = '';
      },
      fileUpload: async function (file) {
        const form = new FormData();
        form.append('attachment', file);
        const response = await fetch(`/api/order/${this.order.id}/attachment`, {
          method: 'POST',
          body: form
        });

        switch (response.status) {
          case 201:
            const json = await response.json();
            this.order.attachments.push(json);
            this.order._attachmentError = '';
            break;
          case 413:
            this.order._attachmentError = this.$t('orderList.attachmentTooLarge');
            break;
          default:
            this.order._attachmentError = '';
        }
      },
      getFileIcon(name) {
        const type = mime.getType(name);

        if (type === null) {
          return 'file';
        }

        const category = type.split('/')[0];

        if (category === 'image') {
          return 'file-image';
        }

        if (category === 'audio') {
          return 'file-audio';
        }

        if (category === 'video') {
          return 'file-video';
        }

        if (type === 'text/csv') {
          return 'file-csv';
        }

        if (type === 'application/pdf') {
          return 'file-pdf';
        }

        if ([
          'application/vnd.ms-excel',
          'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
        ].includes(type)) {
          return 'file-excel';
        }

        if ([
          'application/msword',
          'application/vnd.openxmlformats-officedocument.wordprocessingml.document'
        ].includes(type)) {
          return 'file-word';
        }

        if ([
          'application/vnd.ms-powerpoint',
          'application/vnd.openxmlformats-officedocument.presentationml.presentation'
        ].includes(type)) {
          return 'file-powerpoint';
        }

        if ([
          'application/zip',
          'application/vnd.rar',
          'application/gzip',
          'application/x-7z-compressed'
        ].includes(type)) {
          return 'file-zipper';
        }

        return 'file';
      }
    }
  }
</script>
